/**
 * Endpoint de detalle de comprobante — backend REAL (everex-traza-back).
 *
 * GET /api/despacho/comprobante/:referencia?tipo=... → header + items + partidas
 * (con N°serie/lote/vencimiento) según el tipo de la tarea.
 */

import {
  ComprobanteDetalleSchema,
  PickeoRegistradoSchema,
  type ComprobanteDetalle,
  type PickeoRegistrado,
} from '@/api/contracts/comprobantes';
import { apiFetch } from '@/api/http';

/** Tipos válidos del comprobante para el endpoint de detalle. */
export type ComprobanteTipo = ComprobanteDetalle['tipo'];

export async function getComprobante(
  referencia: string,
  tipo: ComprobanteTipo,
): Promise<ComprobanteDetalle> {
  const data = await apiFetch<unknown>(
    `/despacho/comprobante/${encodeURIComponent(referencia)}?tipo=${encodeURIComponent(tipo)}`,
    { auth: true },
  );
  return ComprobanteDetalleSchema.parse(data);
}

/** Response cruda persistida en `erp_task.erp_response` — JSON exacto que
 *  ACSIS devolvió al consultar el detalle al momento del último sync. */
export interface ComprobanteRaw {
  /** ID en ACSIS de la tarea (erp_task.erp_id) — el que se muestra como
   *  "ID (ACSIS)" en el detalle. */
  erpId: string | null;
  /** Referencia consultada — eco para que el modal arme su título. */
  referencia: string;
  /** ISO de cuando la tarea entró por primera vez a nuestro sistema
   *  (erp_task.created_at). Más útil que el last_sync para el operador. */
  importadoAt: string | null;
  /** JSON tal como lo devolvió ACSIS. Estructura opaca a propósito. */
  response: unknown;
}

export async function getComprobanteRaw(
  referencia: string,
  tipo: ComprobanteTipo,
): Promise<ComprobanteRaw> {
  return apiFetch<ComprobanteRaw>(
    `/despacho/comprobante/${encodeURIComponent(referencia)}/raw?tipo=${encodeURIComponent(tipo)}`,
    { auth: true },
  );
}

/** Una entry del log de cierre de tarea (cumplir_tarea.*) desde audit_log. */
export interface CumplirTareaLogEntry {
  id: number;
  /** 'cumplir_tarea.iniciada' | 'cumplir_tarea.success' | 'cumplir_tarea.error' */
  action: string;
  erpId: string | null;
  createdAt: string | null;
  /** metadata: request en `iniciada`, response en success/error. */
  metadata: unknown;
}

/**
 * Log de cierre de tarea en el ERP (request + response) leído del audit_log.
 * `erpId` = id de la tarea en ACSIS (erp_task.erp_id).
 */
export async function getCumplirTareaLog(erpId: string): Promise<CumplirTareaLogEntry[]> {
  const data = await apiFetch<{ entries: CumplirTareaLogEntry[] }>(
    `/despacho/cumplir-tarea-log/${encodeURIComponent(erpId)}`,
    { auth: true },
  );
  return data.entries ?? [];
}

/** Resultado del backfill de product_var sobre el stock de un despacho. */
export interface VincularProductVarResult {
  total: number;
  vinculadas: number;
  sinResolver: string[];
}

/** Resultado del resync (gln_destino + product_var + depósito + stock) de un despacho. */
export interface ResincronizarResult {
  glnDestino: string | null;
  skusTotal: number;
  gtinResueltos: number;
  sinGtin: string[];
  /** SKUs a los que se les resolvió y guardó el nombre del depósito de origen. */
  depositosResueltos?: number;
  /** Reconciliación del stock contra el ERP fresco. */
  stock?: {
    /** Series/partidas nuevas dadas de alta + sembradas. */
    agregadas: number;
    /** Series que salieron del remito → ingreso cancelado. */
    canceladas: number;
    /** Series reservadas para este remito (nuevas + desde inventario). */
    reservadas: number;
    /** Series que salieron pero ya estaban egresadas (no se tocan). */
    egresadasNoTocadas: number;
    /** Series reservadas por otro remito → reasignables con el botón. */
    conflictos: number;
  };
}

/**
 * Resync liviano del despacho contra el ERP: actualiza GLN destino + GTIN
 * (product_var). No toca stock.
 */
export async function resincronizarDespacho(
  referencia: string,
): Promise<ResincronizarResult> {
  return apiFetch<ResincronizarResult>(
    `/despacho/comprobante/${encodeURIComponent(referencia)}/resincronizar`,
    { method: 'POST', auth: true },
  );
}

/** Resultado del chequeo read-only de cambios contra el ERP (previo a trazar). */
export interface VerificarCambiosResult {
  /** true si el comprobante está dado de baja (anulado) en el ERP. Prioritario. */
  anulado: boolean;
  /** Fecha de baja del ERP (`fechaBaja`), si está anulado. */
  fechaBaja: string | null;
  /** true si el remito cambió en el ERP (hay partidas agregadas/removidas). */
  hayCambios: boolean;
  agregadas: number;
  removidas: number;
}

/**
 * Chequeo READ-ONLY: consulta ACSIS y compara las partidas del despacho contra
 * lo guardado. NO aplica nada. Se usa antes de trazar para frenar si el remito
 * cambió en el ERP (y pedir resincronizar).
 */
export async function verificarCambiosDespacho(
  referencia: string,
): Promise<VerificarCambiosResult> {
  return apiFetch<VerificarCambiosResult>(
    `/despacho/comprobante/${encodeURIComponent(referencia)}/verificar-cambios`,
    { auth: true },
  );
}

/** Una serie de este despacho que hoy está tomada por otro remito. */
export interface ReasignableItem {
  serie: string | null;
  gtin: string | null;
  sku: string | null;
  remitoActual: string | null;
  /** 'reservada' (r=1) o 'egresada' (o=1, ya salió por ese remito). */
  estado: 'reservada' | 'egresada';
}

/** Series del despacho reservadas por otros remitos (para el modal de reasignación). */
export async function getReasignables(referencia: string): Promise<ReasignableItem[]> {
  const data = await apiFetch<{ items: ReasignableItem[] }>(
    `/despacho/comprobante/${encodeURIComponent(referencia)}/reasignables`,
    { auth: true },
  );
  return data.items ?? [];
}

/** Reasigna a este despacho las series reservadas por otros remitos. */
export async function reasignarSeries(
  referencia: string,
): Promise<{ reasignadas: number; total: number }> {
  return apiFetch<{ reasignadas: number; total: number }>(
    `/despacho/comprobante/${encodeURIComponent(referencia)}/reasignar`,
    { method: 'POST', auth: true },
  );
}

/** Toma para este despacho las series que están en inventario libre (a=1). */
export async function asignarDisponibles(
  referencia: string,
): Promise<{ asignadas: number }> {
  return apiFetch<{ asignadas: number }>(
    `/despacho/comprobante/${encodeURIComponent(referencia)}/asignar-disponibles`,
    { method: 'POST', auth: true },
  );
}

/**
 * Qué pasa con las unidades al cancelar la traza:
 *   - `retrazar`: siguen en el depósito y se vuelve a trazar el remito.
 *   - `liberar`: siguen en el depósito y el remito no sale. Quedan reservadas
 *     hasta que Plataforma muestre el remito anulado; después, disponibles.
 *   - `en_transito`: salieron y vuelven; quedan no disponibles hasta el reingreso.
 */
export type DestinoAnulacionEgreso = 'retrazar' | 'liberar' | 'en_transito';

export interface CancelarTrazaResult {
  destino: DestinoAnulacionEgreso;
  /** Transacciones ANMAT enviadas a cancelar (sin las ya canceladas antes). */
  transacciones: string[];
  /** Jobs de cancelación creados en riga (0 si ya estaban todas canceladas). */
  jobs: number;
}

/**
 * Cancelar traza: informa a ANMAT la cancelación de las transacciones de la
 * traza (asíncrono, 202). El egreso local se anula cuando terminan los jobs;
 * el progreso llega en `cancelacion` del detalle de la traza.
 */
export async function cancelarTraza(
  referencia: string,
  destino: DestinoAnulacionEgreso,
): Promise<CancelarTrazaResult> {
  return apiFetch<CancelarTrazaResult>(
    `/despacho/comprobante/${encodeURIComponent(referencia)}/cancelar-traza`,
    { method: 'POST', body: { destino, confirma: true }, auth: true },
  );
}

/**
 * Verifica en ANMAT el estado de cada transacción de una cancelación que riga
 * no terminó o que terminó con errores. Si quedaron todas canceladas, el back
 * anula el egreso.
 */
export async function verificarCancelacionAnmat(
  referencia: string,
): Promise<{ estado: 'en_curso' | 'completa' | 'error' }> {
  return apiFetch<{ estado: 'en_curso' | 'completa' | 'error' }>(
    `/despacho/comprobante/${encodeURIComponent(referencia)}/verificar-cancelacion-anmat`,
    { method: 'POST', auth: true },
  );
}

/**
 * Verifica en Plataforma que el remito de una traza cancelada esté dado de
 * baja y, si lo está, lo anula en RigaTraza (libera las unidades). Si sigue
 * vigente responde 409 `REMITO_VIGENTE_EN_ERP`.
 */
export async function verificarBajaErp(
  referencia: string,
): Promise<{ fechaBaja: string | null; liberadas: number }> {
  return apiFetch<{ fechaBaja: string | null; liberadas: number }>(
    `/despacho/comprobante/${encodeURIComponent(referencia)}/verificar-baja-erp`,
    { method: 'POST', auth: true },
  );
}

/**
 * Carga manual del GLN destino de un despacho (13 dígitos). Se persiste en
 * `erp_task.gln_destino` — la misma columna de la que lee la traza.
 */
export async function setGlnDestino(
  referencia: string,
  glnDestino: string,
): Promise<{ glnDestino: string }> {
  return apiFetch<{ glnDestino: string }>(
    `/despacho/comprobante/${encodeURIComponent(referencia)}/gln-destino`,
    { method: 'POST', auth: true, body: { glnDestino } },
  );
}

/**
 * Vincula las filas de stock del despacho que quedaron sin `product_var_id`
 * (resuelve/crea el product_var desde el ERP por SKU y updatea el stock).
 */
export async function vincularProductVar(
  referencia: string,
): Promise<VincularProductVarResult> {
  return apiFetch<VincularProductVarResult>(
    `/despacho/comprobante/${encodeURIComponent(referencia)}/vincular-product-var`,
    { method: 'POST', auth: true },
  );
}

// =============================================================================
// Pickeo por escaneo (stock a → r → p)
// =============================================================================

/**
 * Registra una unidad pickeada. El back vuelve a validar artículo, lote, cupo
 * y el estado de la serie; si la rechaza, responde 409 con el motivo.
 */
export async function registrarPickeo(
  referencia: string,
  lectura: { gtin: string; lote: string; serie: string },
): Promise<PickeoRegistrado> {
  const data = await apiFetch<{ pickeo: unknown }>(
    `/despacho/comprobante/${encodeURIComponent(referencia)}/pickeos`,
    { method: 'POST', body: lectura, auth: true },
  );
  return PickeoRegistradoSchema.parse(data.pickeo);
}

/** Cancela el pickeo de una unidad (CANCELACION_PICKING y, si corresponde, LIBERACION_RESERVA). */
export async function cancelarPickeo(
  referencia: string,
  stockId: number,
): Promise<{ eventos: string[] }> {
  return apiFetch<{ eventos: string[] }>(
    `/despacho/comprobante/${encodeURIComponent(referencia)}/pickeos/${stockId}`,
    { method: 'DELETE', auth: true },
  );
}
